import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import { getCurrentUser } from "../services/auth";
import { getMyAccess } from "../services/accessControl";

const DEFAULT_FEATURE_ACCESS = {
  "product_details.price_history": { enabled: true, value: { max_rows: null } },
  "product_details.product_intelligence": { enabled: false, value: {} },
  "product_details.nutrition": { enabled: true, value: {} },
  "product_details.ingredients": { enabled: true, value: {} },
  "product_details.additional_info": { enabled: true, value: {} },
};

const AccessContext = createContext({
  loading: true,
  access: null,
  canAccessPath: () => false,
  getFeature: (featureKey) => DEFAULT_FEATURE_ACCESS[featureKey] || { enabled: false, value: {} },
});

const normalizePath = (path) => {
  const normalized = String(path || "/").split("?")[0].replace(/\/+$/, "");
  return normalized || "/";
};

function pathAllowed(access, pathname) {
  const normalized = normalizePath(pathname);
  return (access?.pages || []).some((page) => {
    if (!page.can_access) return false;
    return Boolean(matchPath({ path: page.page_key, end: true }, normalized));
  });
}

export function AccessProvider({ children }) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function loadAccess() {
      setLoading(true);
      try {
        const data = await getMyAccess();
        if (!cancelled) setAccess(data);
      } catch {
        if (!cancelled) setAccess(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({
    loading,
    access,
    canAccessPath: (path) => pathAllowed(access, path),
    getFeature: (featureKey) => (
      access?.features?.[featureKey] || DEFAULT_FEATURE_ACCESS[featureKey] || { enabled: false, value: {} }
    ),
  }), [access, loading]);

  return (
    <AccessContext.Provider value={value}>
      {children}
    </AccessContext.Provider>
  );
}

export const useAccess = () => useContext(AccessContext);

function AccessDenied() {
  const navigate = useNavigate();

  return (
    <div className="page-loading page-loading--denied" role="alert">
      <div className="page-loading__panel">
        <p className="page-loading__eyebrow">Access unavailable</p>
        <h1 className="page-loading__title">This page is not available for your current package.</h1>
        <button
          type="button"
          className="page-loading__button"
          onClick={() => navigate("/dashboard", { replace: true })}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export default function PermissionRoute({ children }) {
  const location = useLocation();
  const user = getCurrentUser();
  const { loading, access, canAccessPath } = useAccess();

  if (!user) return <Navigate to="/login" replace />;
  if (loading) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Checking access...
      </div>
    );
  }
  if (!access || !canAccessPath(location.pathname)) return <AccessDenied />;

  return <>{children}</>;
}

export function PermissionOutlet() {
  return (
    <PermissionRoute>
      <Outlet />
    </PermissionRoute>
  );
}
